import { jsx as _jsx } from "react/jsx-runtime";
/**
 * Cloudflare Turnstile widget for public, unauthenticated forms (§25.3).
 *
 * Renders NOTHING unless VITE_TURNSTILE_SITE_KEY is set at build time — so
 * by default (key unset) this is invisible and the parent form behaves
 * exactly as before. Pairs with the backend app.core.captcha guard, which
 * is likewise a no-op until its secret + the tenant feature flag are on.
 *
 * Activation is a config-only flip: set VITE_TURNSTILE_SITE_KEY (frontend)
 * + turnstile_secret_key (backend) + the captcha_public_forms flag.
 */
import { useEffect, useRef } from 'react';
const SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY;
const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
/** Whether CAPTCHA is configured on the frontend at all. Lets callers gate
 *  submit-button logic ("require a token") without hard-coding the env read. */
export const captchaEnabled = Boolean(SITE_KEY);
function loadScript() {
    if (document.querySelector(`script[src="${SCRIPT_SRC}"]`))
        return Promise.resolve();
    return new Promise((resolve, reject) => {
        const s = document.createElement('script');
        s.src = SCRIPT_SRC;
        s.async = true;
        s.defer = true;
        s.onload = () => resolve();
        s.onerror = () => reject(new Error('Failed to load Turnstile'));
        document.head.appendChild(s);
    });
}
export function Turnstile({ onToken }) {
    const ref = useRef(null);
    const rendered = useRef(false);
    useEffect(() => {
        if (!SITE_KEY || !ref.current || rendered.current)
            return;
        let cancelled = false;
        loadScript()
            .then(() => {
            // The script may need a tick to install window.turnstile.
            const tryRender = () => {
                const api = window.turnstile;
                if (cancelled || !ref.current || rendered.current)
                    return;
                if (!api) {
                    window.setTimeout(tryRender, 150);
                    return;
                }
                rendered.current = true;
                api.render(ref.current, {
                    sitekey: SITE_KEY,
                    callback: (t) => onToken(t),
                    'expired-callback': () => onToken(null),
                });
            };
            tryRender();
        })
            .catch(() => onToken(null));
        return () => {
            cancelled = true;
        };
    }, [onToken]);
    if (!SITE_KEY)
        return null;
    return _jsx("div", { ref: ref, className: "mt-4 flex justify-center" });
}
